import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';

export const COLORS = ['#D94E18', '#F97316', '#FBBF24', '#34D399', '#60A5FA', '#A78BFA', '#F472B6', '#94A3B8', '#6366F1', '#EC4899'];

export function KPICard({ title, value, subtitle, icon: Icon, trend, color = 'zinc' }) {
  const colorMap = {
    green: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-[#D94E18]',
    blue: 'bg-blue-50 text-blue-600',
    zinc: 'bg-zinc-50 text-zinc-600',
  };
  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-zinc-900 mt-1 font-mono">{value}</p>
            {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorMap[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-zinc-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name || p.dataKey} className="font-mono" style={{ color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

export function acctAmtSigned(val) {
  if (val === 0 || val === undefined || val === null) return '-';
  if (val < 0) return `(${formatCurrency(Math.abs(val))})`;
  return formatCurrency(val);
}

export function StatementHeader({ title, period, asOf, entityName }) {
  return (
    <div className="text-center mb-6 pb-4 border-b-2 border-zinc-800">
      <p className="text-sm font-semibold text-zinc-500 tracking-widest uppercase">{entityName || 'kaya.ai'}</p>
      <h2 className="text-xl font-bold text-zinc-900 mt-1">{title}</h2>
      {period && <p className="text-sm text-zinc-500 mt-1">{period.start_date || 'Beginning'} to {period.end_date || 'Current'}</p>}
      {asOf && <p className="text-sm text-zinc-500 mt-1">As of {asOf}</p>}
    </div>
  );
}

export function SectionRow({ label, amount, pyAmount, indent = false, bold = false, total = false, grandTotal = false, negative = false, className = '' }) {
  const rowCls = grandTotal
    ? 'border-t-2 border-zinc-900 border-b-4 border-double border-zinc-900 bg-zinc-100'
    : total
    ? 'border-t border-zinc-300 bg-zinc-50'
    : 'border-b border-zinc-200 hover:bg-zinc-50/50';
  const textCls = bold || total || grandTotal ? 'font-semibold' : '';
  const amtColor = negative || (amount && amount < 0) ? 'text-red-600' : 'text-zinc-900';
  return (
    <tr className={`${rowCls} ${className}`}>
      <td className={`py-2 px-4 ${indent ? 'pl-10' : 'pl-4'} ${textCls} text-sm text-zinc-700`}>{label}</td>
      <td className={`py-2 px-4 pr-6 text-right font-mono text-sm tabular-nums ${textCls} ${amtColor}`}>{amount !== undefined ? acctAmtSigned(amount) : ''}</td>
      {pyAmount !== undefined && (
        <td className={`py-2 px-4 pr-6 text-right font-mono text-sm tabular-nums text-zinc-400 ${textCls}`}>{acctAmtSigned(pyAmount)}</td>
      )}
    </tr>
  );
}
